import { ArrowRight, Check } from "lucide-react";
import { ScrollReveal, SectionLabel } from "./Reveal";

const packages = [
  {
    name: "Starter",
    price: "£800",
    tag: null,
    bestFor: "Barbers · Cafés · Personal brands",
    included: ["1 shoot day / month", "8 short-form vertical edits", "1 hero brand cut (up to 45s)", "Caption + hook writing", "Monthly reporting Loom"],
    addons: "Paid boost setup · Extra shoot day",
  },
  {
    name: "Growth",
    price: "£1,600",
    tag: "Most Popular",
    bestFor: "Restaurants · Gyms · Clothing brands",
    included: ["2 shoot days / month", "16 short-form edits", "2 hero cuts (up to 60s each)", "Full social calendar & scheduling", "Community reply support (Mon–Fri)", "Monthly strategy call"],
    addons: "Meta ads setup · Photography add-on",
  },
  {
    name: "Flagship",
    price: "£3,200",
    tag: null,
    bestFor: "Hotels · Car dealerships · Real estate",
    included: ["3 shoot days / month", "24 short-form edits + 1 monthly brand film", "Full social calendar, scheduling & replies", "Meta & TikTok ads management (ad spend not incl.)", "Landing page video + retargeting cuts", "Weekly performance report"],
    addons: "YouTube ads · Photography add-on · Extra editor",
  },
];

export const PackagesSection = () => (
  <section data-testid="packages-section" className="py-24 lg:py-32 bg-[#111111] border-y border-white/10">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
      <ScrollReveal>
        <SectionLabel index="03" title="Monthly Packages" />
        <h2 className="font-display leading-[0.95] text-5xl sm:text-6xl lg:text-7xl max-w-4xl">
          Always-on content.
        </h2>
        <p className="mt-6 text-base md:text-lg text-[#8A8A85] max-w-xl">
          Not just filming — I run your social media and ads too. One retainer, one calendar, everything handled.
        </p>
      </ScrollReveal>

      <div className="mt-16 grid lg:grid-cols-3 gap-6">
        {packages.map((p, i) => (
          <ScrollReveal key={p.name} delay={i * 0.08} className="h-full">
            <div
              data-testid={`package-card-${p.name.toLowerCase()}`}
              className={`relative h-full flex flex-col border p-8 lg:p-10 bg-[#0A0A0A] transition-transform duration-500 hover:-translate-y-2 ${
                p.tag ? "border-[#F2F0EB]" : "border-white/10"
              }`}
            >
              {p.tag && (
                <span className="absolute -top-3 left-8 bg-[#F2F0EB] text-[#0A0A0A] font-mono2 text-[10px] uppercase tracking-[0.2em] px-3 py-1">
                  {p.tag}
                </span>
              )}
              <h3 className="font-display text-3xl">{p.name}</h3>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-display text-5xl lg:text-6xl">{p.price}</span>
                <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A8A85]">/ month</span>
              </div>

              <div className="mt-8">
                <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#55554F] mb-2">Best for</p>
                <p className="text-sm text-[#F2F0EB]">{p.bestFor}</p>
              </div>

              <div className="mt-6 flex-1">
                <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#55554F] mb-3">Included</p>
                <ul className="space-y-2.5">
                  {p.included.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-[#8A8A85]">
                      <Check className="w-4 h-4 text-[#F2F0EB] mt-0.5 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6">
                <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#55554F] mb-2">Add-ons</p>
                <p className="text-xs text-[#8A8A85]">{p.addons}</p>
              </div>

              <button
                data-testid={`package-book-${p.name.toLowerCase()}`}
                onClick={() => window.__openEnquiry()}
                className={`group mt-8 w-full py-4 font-display text-sm tracking-[0.08em] flex items-center justify-center gap-3 transition-colors duration-300 ${
                  p.tag
                    ? "bg-[#F2F0EB] text-[#0A0A0A] hover:bg-white"
                    : "border border-white/20 text-[#F2F0EB] hover:bg-[#F2F0EB] hover:text-[#0A0A0A]"
                }`}
              >
                Book a Call
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1.5 transition-transform duration-300" />
              </button>
            </div>
          </ScrollReveal>
        ))}
      </div>

      <ScrollReveal delay={0.1}>
        <div
          data-testid="package-card-all-in-one"
          className="mt-6 border border-[#F2F0EB] bg-[#F2F0EB] text-[#0A0A0A] p-8 lg:p-12 grid lg:grid-cols-12 gap-8 items-center"
        >
          <div className="lg:col-span-5">
            <span className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#55554F]">Everything handled</span>
            <h3 className="font-display text-4xl lg:text-5xl mt-4">The All-In-One</h3>
            <p className="text-sm text-[#0A0A0A]/70 mt-4 max-w-md leading-relaxed">
              Not just filming — I run your social media and your ads too. One team, one
              calendar, one invoice. You run the business; I make sure the world sees it.
            </p>
          </div>
          <div className="lg:col-span-4">
            <ul className="space-y-2.5">
              {[
                "Everything in Flagship",
                "4 shoot days / month",
                "Meta, TikTok & YouTube ads managed",
                "Landing page video + retargeting cuts",
                "Weekly reports + dedicated strategy",
              ].map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm">
                  <Check className="w-4 h-4 mt-0.5 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-3 lg:text-right">
            <div className="flex lg:justify-end items-baseline gap-2">
              <span className="font-display text-5xl lg:text-6xl">£4,800</span>
              <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#55554F]">/ month</span>
            </div>
            <button
              data-testid="package-book-all-in-one"
              onClick={() => window.__openEnquiry()}
              className="group mt-6 inline-flex items-center gap-3 bg-[#0A0A0A] text-[#F2F0EB] font-display text-sm tracking-[0.08em] px-8 py-4 hover:scale-105 transition-transform duration-300"
            >
              Book a Call
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1.5 transition-transform duration-300" />
            </button>
          </div>
        </div>
      </ScrollReveal>

      <ScrollReveal delay={0.1}>
        <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#55554F]">
            Ad spend billed separately. · Minimum 3-month term.
          </p>
          <button
            data-testid="packages-discovery-call-btn"
            onClick={() => window.__openEnquiry()}
            className="group flex items-center gap-3 font-display text-sm tracking-[0.08em] border-b border-[#F2F0EB] pb-2 hover:gap-5 transition-all duration-300"
          >
            Book a Discovery Call
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </ScrollReveal>
    </div>
  </section>
);
